import { type GridSortDirection, type GridSortModel } from "@mui/x-data-grid-pro";
import {
    type ColumnDefinition,
    type UseDataGridFilteringReturn,
    type UseDataGridPagingReturn,
    type UseDataGridSortingReturn,
    type UserDataGridFullTextFilterReturn,
} from "@pmag/ui-components";
import {
    type GQLCompatibleArticleSort,
    type GQLCompatibleArticleSortField,
    GQLSortDirection,
    type GQLUsageListQueryVariables,
} from "@src/graphql.generated";

export type DataGridQueryInputVariablesOptions = {
    articleDescription?: string | null;
    articleNumber?: string | null;
    columns: ColumnDefinition<string, string>[];
    filtering?: UseDataGridFilteringReturn;
    fullTextFilter?: UserDataGridFullTextFilterReturn;
    paging?: UseDataGridPagingReturn;
    sorting?: UseDataGridSortingReturn;
};

export const resolveDataGridQueryInputVariables = ({
    articleNumber,
    articleDescription,
    columns,
    fullTextFilter,
    paging,
    sorting,
}: DataGridQueryInputVariablesOptions): GQLUsageListQueryVariables => {
    return {
        articleNumber,
        articleDescription,
        modelSearch: fullTextFilter?.debouncedValue && fullTextFilter.debouncedValue.length > 0 ? fullTextFilter.debouncedValue : undefined,
        offset: (paging?.paginationModel?.page ?? 0) * (paging?.paginationModel?.pageSize ?? 25),
        limit: paging?.paginationModel?.pageSize ?? 25,
        sort: sortModelToGQLSortItem({ columns, sortModel: sorting?.sortModel }),
    };
};

export const gridSortDirectionToGQLSortingDirection = (sortDirection: GridSortDirection): GQLSortDirection => {
    if (sortDirection === "asc") {
        return GQLSortDirection.ASC;
    } else if (sortDirection === "desc") {
        return GQLSortDirection.DESC;
    }

    return GQLSortDirection.ASC;
};

export const sortModelToGQLSortItem = ({
    columns,
    sortModel,
}: {
    columns: ColumnDefinition<string, string>[];
    sortModel?: GridSortModel;
}): Array<GQLCompatibleArticleSort> => {
    if (!sortModel) {
        return [];
    }

    return sortModel.reduce((accumulator, model): Array<GQLCompatibleArticleSort> => {
        const columnDefinition = columns.find((column) => {
            return column.field === model.field;
        });

        if (columnDefinition) {
            const mappedField: GQLCompatibleArticleSortField | null = columnDefinition.apiMapping?.sortFieldName as GQLCompatibleArticleSortField;
            const mappedDirection = gridSortDirectionToGQLSortingDirection(model.sort);
            if (mappedField) {
                const item: GQLCompatibleArticleSort = { direction: mappedDirection, field: mappedField };

                return [...accumulator, item];
            }
        }

        return accumulator;
    }, []);
};
